// sales-flow-v2
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { ClientFlowBar } from "@/components/ClientFlowBar";
import { PageStatCards } from "@/components/PageStatCards";
import { TableToolbar } from "@/components/TableToolbar";
import { RowActions } from "@/components/RowActions";
import { EmptyState } from "@/components/EmptyState";
import { UomSelect } from "@/components/UomSelect";
import { FieldError } from "@/components/FieldError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { fmtNum, fmtDate } from "@/lib/format";
import { openPdf } from "@/lib/print";
import { getErrorMessage, getFieldErrors } from "@/lib/errors";
import { usePermissions } from "@/lib/permissions";

import { confirmAction } from "@/lib/dialogs";
export const money = (v) => (v === null || v === undefined || v === "" ? "—" : `₹ ${fmtNum(v, 2)}`);
export { fmtDate };

const TONE = {
  WON: "default", ACCEPTED: "default", APPROVED: "default", CHANGES: "destructive", ACTIVE: "default", COMPLETED: "default",
  LOST: "destructive", REJECTED: "destructive", CANCELLED: "destructive", TERMINATED: "destructive",
};
export function StateBadge({ value }) {
  return <Badge variant={TONE[value] || "secondary"}>{(value || "").replaceAll("_", " ")}</Badge>;
}

const blank = (fields) => Object.fromEntries(fields.map((f) => [f.key, f.type === "items" || f.type === "measurements" ? [] : (f.default ?? "")]));

const priceFromCost = (cost, margin) => {
  const m = Number(margin) || 0;
  return cost > 0 && m > 0 && m < 100 ? Math.round((cost / (1 - m / 100)) * 100) / 100 : "";
};

function ItemsEditor({ value, onChange, activities = [], targetMargin = "" }) {
  const rows = value || [];
  const [tplId, setTplId] = useState("");
  const [tab, setTab] = useState("client");   // client = what the client sees, internal = cost and margin
  const byId = Object.fromEntries(activities.map((x) => [x.id, x]));
  const templates = [...new Map(activities.map((x) => [x.template_id, x.template_name])).entries()];
  const loadTemplate = async () => {
    const acts = activities.filter((x) => x.template_id === tplId);
    if (!acts.length) return;
    if (rows.some((r) => r.description) && !await confirmAction("Add the template lines below your existing lines?")) return;
    const lines = acts.map((a) => ({
      description: a.name, uom: a.uom || "", quantity: a.quantity ?? "", rate: priceFromCost(Number(a.cost_rate), targetMargin),
      template_item_id: a.id, cost_rate: "", _section: a.group_name || a.category,
    }));
    onChange([...rows.filter((r) => r.description), ...lines]);
  };
  const fillRates = () => onChange(rows.map((r) => {
    const c = Number(r.cost_rate !== "" && r.cost_rate != null ? r.cost_rate : byId[r.template_item_id]?.cost_rate || 0);
    return !Number(r.rate) && c > 0 && Number(targetMargin) > 0 ? { ...r, rate: priceFromCost(c, targetMargin) } : r;
  }));
  const multi = new Set(activities.map((x) => x.template_name)).size > 1;
  const upd = (i, patch) => onChange(rows.map((r, k) => (k === i ? { ...r, ...patch } : r)));
  const pick = (i, id) => {
    const act = byId[id];
    if (!id || !act) return upd(i, { template_item_id: "", cost_rate: "" });
    const r = rows[i];
    upd(i, { template_item_id: id, cost_rate: "", description: r.description || act.name, uom: r.uom || act.uom || "" });
  };
  const costOf = (r) => (r.cost_rate !== "" && r.cost_rate != null ? Number(r.cost_rate) : Number(byId[r.template_item_id]?.cost_rate || 0));
  const amount = (r) => (Number(r.quantity) || 0) * (Number(r.rate) || 0);
  const cost = (r) => (Number(r.quantity) || 0) * costOf(r);
  const total = rows.reduce((t, r) => t + amount(r), 0);
  const totalCost = rows.reduce((t, r) => t + cost(r), 0);
  const margin = total - totalCost;
  const isBelowCost = (r) => (Number(r.quantity) || 0) > 0 && costOf(r) > 0 && amount(r) < cost(r);
  const belowCost = rows.map((r, i) => (isBelowCost(r) ? i + 1 : null)).filter(Boolean);
  // the library cost is per the activity's own unit; a different unit on the line means that cost may not fit this quantity
  const unitDiffers = (r) => { const a = byId[r.template_item_id]; return !!(a && a.uom && r.uom && String(a.uom).trim().toLowerCase() !== String(r.uom).trim().toLowerCase()); };
  const noCost = rows.filter((r) => r.description && costOf(r) <= 0).length;
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2 rounded-md border border-dashed border-border p-2">
        <Select value={tplId || "none"} onValueChange={(v) => setTplId(v === "none" ? "" : v)}>
          <SelectTrigger className="w-72"><SelectValue placeholder="Choose a template" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">— choose a template —</SelectItem>
            {templates.map(([id, name]) => <SelectItem key={id} value={id}>{name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button type="button" variant="outline" size="sm" disabled={!tplId} onClick={loadTemplate}>Load lines from template</Button>
        <Button type="button" variant="outline" size="sm" disabled={!Number(targetMargin)} onClick={fillRates}
          title="Sets the sell rate of lines that have none: cost / (1 - target margin)">Fill empty sell rates ({Number(targetMargin) || 0}% margin)</Button>
      </div>
      {belowCost.length > 0 && (
        <div role="alert" className="rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-800 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-200" data-testid="below-cost-banner">
          <strong>{belowCost.length} line{belowCost.length === 1 ? "" : "s"} sell below cost</strong> (line{belowCost.length === 1 ? "" : "s"} {belowCost.join(", ")}). Check the quantity, unit, cost rate and sell rate on {belowCost.length === 1 ? "it" : "each"}; the Internal cost tab shows the margin.
        </div>
      )}
      <div className="flex gap-1 border-b border-border" role="tablist">
        {[["client", "Client quotation"], ["internal", "Internal cost"]].map(([k, label]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${tab === k ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>{label}</button>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        {tab === "client" ? "What the client receives: sell rates with the margin already inside. No cost and no margin are shown here."
          : "Only your team sees this: your cost rates with no margin, and the profit on each line."}
      </p>
      <div className="max-h-80 overflow-auto rounded-md border border-border">
        {tab === "client" ? (
          <Table className="min-w-[860px]">
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">#</TableHead><TableHead className="min-w-[280px]">Description</TableHead>
                <TableHead className="min-w-[110px]">Unit</TableHead><TableHead className="min-w-[110px]">Qty</TableHead>
                <TableHead className="min-w-[130px]">Rate</TableHead><TableHead className="min-w-[140px] text-right">Amount</TableHead><TableHead className="w-8" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r, i) => {
                const header = r._section && r._section !== rows[i - 1]?._section;
                return (
                  <Fragment key={i}>
                    {header && <TableRow><TableCell colSpan={7} className="bg-muted/40 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{r._section}</TableCell></TableRow>}
                    <TableRow>
                      <TableCell className="text-xs text-muted-foreground">{i + 1}</TableCell>
                      <TableCell><Input value={r.description || ""} onChange={(e) => upd(i, { description: e.target.value })} /></TableCell>
                      <TableCell><UomSelect value={r.uom || ""} onChange={(v) => upd(i, { uom: v })} /></TableCell>
                      <TableCell><Input type="number" step="0.001" value={r.quantity ?? ""} onChange={(e) => upd(i, { quantity: e.target.value })} /></TableCell>
                      <TableCell><Input type="number" step="0.01" value={r.rate ?? ""} onChange={(e) => upd(i, { rate: e.target.value })}
                        className={isBelowCost(r) ? "border-rose-500 focus-visible:ring-rose-500" : ""} title={isBelowCost(r) ? "This sells below its cost" : undefined} aria-invalid={isBelowCost(r) || undefined} /></TableCell>
                      <TableCell className="text-right font-mono text-xs">{money(amount(r))}</TableCell>
                      <TableCell><button type="button" title="Remove line" onClick={() => onChange(rows.filter((_, k) => k !== i))} className="text-muted-foreground hover:text-rose-500"><Trash2 className="h-4 w-4" /></button></TableCell>
                    </TableRow>
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        ) : (
          <Table className="min-w-[1180px]">
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">#</TableHead><TableHead className="min-w-[260px]">Activity (cost source)</TableHead><TableHead className="min-w-[220px]">Description</TableHead>
                <TableHead className="min-w-[90px]">Unit</TableHead><TableHead className="min-w-[110px]">Qty</TableHead>
                <TableHead className="min-w-[120px]">Cost rate</TableHead><TableHead className="min-w-[130px] text-right">Cost amount</TableHead>
                <TableHead className="min-w-[110px] text-right">Sell amount</TableHead><TableHead className="min-w-[90px] text-right">Margin</TableHead><TableHead className="w-8" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r, i) => {
                const amt = amount(r); const m = amt ? ((amt - cost(r)) / amt) * 100 : 0;
                const header = r._section && r._section !== rows[i - 1]?._section;
                return (
                  <Fragment key={i}>
                    {header && <TableRow><TableCell colSpan={10} className="bg-muted/40 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{r._section}</TableCell></TableRow>}
                    <TableRow>
                      <TableCell className="text-xs text-muted-foreground">{i + 1}</TableCell>
                      <TableCell>
                        <Select value={r.template_item_id || "none"} onValueChange={(v) => pick(i, v === "none" ? "" : v)}>
                          <SelectTrigger><SelectValue placeholder="No activity" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">— none (no cost) —</SelectItem>
                            {activities.map((x) => <SelectItem key={x.id} value={x.id}>{x.code} {x.name}{multi ? ` · ${x.template_name}` : ""}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-sm">{r.description || "—"}</TableCell>
                      <TableCell className="text-xs">{r.uom || "—"}{unitDiffers(r) && <span className="block text-[10px] text-amber-600" title="The activity's cost is per its own unit" data-testid="unit-differs">cost is per {byId[r.template_item_id].uom}</span>}</TableCell>
                      <TableCell><Input type="number" step="0.001" value={r.quantity ?? ""} onChange={(e) => upd(i, { quantity: e.target.value })} /></TableCell>
                      <TableCell><Input type="number" step="0.01" placeholder={String(byId[r.template_item_id]?.cost_rate ?? 0)} value={r.cost_rate ?? ""} onChange={(e) => upd(i, { cost_rate: e.target.value })} /></TableCell>
                      <TableCell className="text-right font-mono text-xs">{money(cost(r))}</TableCell>
                      <TableCell className="text-right font-mono text-xs text-muted-foreground">{money(amt)}</TableCell>
                      <TableCell className={`text-right font-mono text-xs ${m < 0 ? "text-rose-500" : ""}`}>{amt ? `${m.toFixed(1)}%` : "—"}</TableCell>
                      <TableCell><button type="button" title="Remove line" onClick={() => onChange(rows.filter((_, k) => k !== i))} className="text-muted-foreground hover:text-rose-500"><Trash2 className="h-4 w-4" /></button></TableCell>
                    </TableRow>
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button type="button" variant="outline" size="sm" onClick={() => onChange([...rows, { description: "", uom: "", quantity: "", rate: "", template_item_id: "", cost_rate: "" }])}><Plus className="h-4 w-4" /> Add line</Button>
        {tab === "client" ? (
          <div className="text-right font-mono text-sm"><div className="font-semibold">Quote total {money(total)}</div></div>
        ) : (
          <div className="text-right font-mono text-sm">
            <div className="font-semibold">Total cost {money(totalCost)}</div>
            <div className="text-xs text-muted-foreground">Quote {money(total)} · Margin {money(margin)} ({total ? ((margin / total) * 100).toFixed(1) : "0.0"}%)</div>
          </div>
        )}
      </div>
      {tab === "internal" && noCost > 0 && <p className="text-xs text-amber-600">{noCost} line(s) have no cost - the margin above is overstated until you pick an activity with cost lines or type a cost rate.</p>}
    </div>
  );
}

const MEASURE_UNITS = ["sq ft", "sq m", "rft", "m", "ft", "floors", "nos", "acres", "cents"];
const MEASURE_SUGGEST = [["Built-up area", "sq ft"], ["Plot area", "sq ft"], ["Carpet area", "sq ft"], ["Floors", "floors"], ["Total height", "ft"]];

/** Overall measurements of the project (built-up area, plot area, floors ...). Recorded on the proposal; rows with no label or value are ignored. */
export function MeasurementsEditor({ value, onChange }) {
  const rows = value || [];
  const setRow = (i, patch) => onChange(rows.map((r, n) => (n === i ? { ...r, ...patch } : r)));
  const add = (label = "", unit = "sq ft") => onChange([...rows, { label, value: "", unit }]);
  const missing = MEASURE_SUGGEST.filter(([l]) => !rows.some((r) => (r.label || "").toLowerCase() === l.toLowerCase()));
  return (
    <div className="mt-1.5 rounded-xl border border-border bg-secondary/30 p-3" data-testid="measurements-box">
      {rows.length === 0 && <p className="mb-2 text-sm text-muted-foreground">No measurements yet. Add the overall size of the project, for example built-up area in sq ft.</p>}
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-[1fr_8rem_7rem_auto] items-center gap-2" data-testid={`measurement-row-${i}`}>
            <Input placeholder="e.g. Built-up area" value={r.label || ""} onChange={(e) => setRow(i, { label: e.target.value })} data-testid={`measurement-label-${i}`} />
            <Input type="number" min="0" step="0.01" placeholder="Value" value={r.value ?? ""} onChange={(e) => setRow(i, { value: e.target.value })} data-testid={`measurement-value-${i}`} />
            <Select value={r.unit || "none"} onValueChange={(u) => setRow(i, { unit: u === "none" ? "" : u })}>
              <SelectTrigger data-testid={`measurement-unit-${i}`}><SelectValue placeholder="Unit" /></SelectTrigger>
              <SelectContent><SelectItem value="none">— unit —</SelectItem>{MEASURE_UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
            </Select>
            <Button type="button" size="icon" variant="ghost" onClick={() => onChange(rows.filter((_, n) => n !== i))} title="Remove"><Trash2 className="h-4 w-4 text-rose-500" /></Button>
          </div>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {missing.map(([l, u]) => <button key={l} type="button" onClick={() => add(l, u)} data-testid={`measurement-add-${l}`} className="rounded-full border border-border bg-background px-3 py-1 text-xs font-medium text-foreground hover:bg-accent">+ {l}</button>)}
        <button type="button" onClick={() => add()} data-testid="measurement-add-other" className="rounded-full border border-dashed border-border px-3 py-1 text-xs font-medium text-muted-foreground hover:bg-accent">+ Other</button>
      </div>
    </div>
  );
}

function Field({ f, form, setForm, ctx, errors }) {
  const v = form[f.key];
  const set = (val) => setForm({ ...form, [f.key]: val });
  let input;
  if (f.type === "textarea") input = <Textarea value={v} onChange={(e) => set(e.target.value)} className="mt-1.5" rows={3} />;
  else if (f.type === "measurements") input = <MeasurementsEditor value={v} onChange={set} />;
  else if (f.type === "items") input = <ItemsEditor value={v} onChange={set} activities={ctx.activities} targetMargin={form.target_margin_pct} />;
  else if (f.type === "client" || f.type === "select") {
    const opts = f.type === "client" ? (ctx.clients || []).map((c) => ({ value: c.id, label: `${c.code} · ${c.name}` })) : f.options.map((o) => ({ value: o, label: o.replaceAll("_", " ") }));
    input = (
      <Select value={v || "none"} onValueChange={(x) => set(x === "none" ? "" : x)}>
        <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
        <SelectContent>
          {f.type === "client" && <SelectItem value="none">— none —</SelectItem>}
          {opts.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  } else input = <Input type={f.type || "text"} step={f.type === "number" ? "0.01" : undefined} required={f.required} value={v} onChange={(e) => set(e.target.value)} className="mt-1.5" />;
  return (
    <div className={f.wide || f.type === "items" || f.type === "textarea" || f.type === "measurements" ? "col-span-2" : ""}>
      <Label>{f.label}</Label>
      {input}
      <FieldError error={errors[f.key]} />
    </div>
  );
}

/**
 * One list page for Leads / Tenders / Proposals / Contracts.
 * cfg: { title, description, endpoint, stateField, states, fields, columns, stats(rows), actions, csv(row), singular }
 */
export function PipelinePage({ cfg, embedded = false, rowFilter = null, onLoaded = null }) {
  const { can } = usePermissions();
  const canEdit = can("RECEIVABLES", "CREATE");     // View-only roles see the list but no New / Edit / Delete / step buttons
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("");
  const [dialog, setDialog] = useState(null);       // {row|null}
  const [form, setForm] = useState(blank(cfg.fields));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [pick, setPick] = useState(null);           // {action,row}
  const [pickId, setPickId] = useState("");
  const [act, setAct] = useState(null);             // {a,row} - an action that asks for a few details first (a.form)
  const [actData, setActData] = useState({});
  const [actErr, setActErr] = useState({});
  const [actBusy, setActBusy] = useState(false);
  const [actMsg, setActMsg] = useState("");
  const [custom, setCustom] = useState(null);       // {a,row} - an action with its own dialog component (a.dialog)

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, c] = await Promise.all([api.get(`/pipeline/${cfg.endpoint}`), api.get("/revenue/clients").catch(() => ({ data: [] }))]);
      setRows(r.data); setClients(c.data); if (onLoaded) onLoaded();
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setLoading(false); }
  }, [cfg.endpoint]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (cfg.actions?.some((a) => a.needsProject)) api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {});
  }, [cfg.actions]);

  useEffect(() => {
    if (cfg.needsActivities) api.get("/pipeline/activities").then(({ data }) => setActivities(data)).catch(() => {});
  }, [cfg.needsActivities]);

  const ctx = { clients, projects, activities, reload: load };
  const clientName = (id) => clients.find((c) => c.id === id)?.name || "—";
  const shown = useMemo(() => {
    const q = search.toLowerCase();
    return rows.filter((r) => (!rowFilter || rowFilter(r)) && (!filter || r[cfg.stateField] === filter) &&
      (!q || `${r.code} ${r.title} ${clientName(r.client_id)}`.toLowerCase().includes(q)));
  }, [rows, search, filter, clients, rowFilter]);  // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => { setForm(blank(cfg.fields)); setErrors({}); setDialog({ row: null }); };
  const openEdit = (row) => {
    const f = blank(cfg.fields);
    cfg.fields.forEach((fd) => { if (row[fd.key] !== null && row[fd.key] !== undefined) f[fd.key] = fd.type === "items" || fd.type === "measurements" ? row[fd.key].map((i) => ({ ...i })) : row[fd.key]; });
    setForm(f); setErrors({}); setDialog({ row });
  };

  const submit = async (e) => {
    e.preventDefault(); setErrors({}); setSaving(true);
    const body = {};
    cfg.fields.forEach((fd) => {
      const val = form[fd.key];
      if (fd.type === "items") body[fd.key] = (val || []).filter((i) => i.description).map((i) => ({ description: i.description, uom: i.uom || null, quantity: Number(i.quantity) || 0, rate: Number(i.rate) || 0, template_item_id: i.template_item_id || null, cost_rate: i.cost_rate === "" || i.cost_rate == null ? null : Number(i.cost_rate) }));
      else if (fd.type === "measurements") body[fd.key] = (val || []).filter((m) => (m.label || "").trim() && m.value !== "" && m.value != null).map((m) => ({ label: m.label.trim(), value: Number(m.value) || 0, unit: m.unit || null }));
      else if (fd.type === "number") body[fd.key] = val === "" ? (fd.nullable === false ? 0 : null) : Number(val);
      else body[fd.key] = val === "" ? null : val;
    });
    try {
      if (dialog.row) await api.patch(`/pipeline/${cfg.endpoint}/${dialog.row.id}`, body);
      else await api.post(`/pipeline/${cfg.endpoint}`, body);
      toast.success(`${cfg.singular} saved`); setDialog(null); load();
    } catch (err) { setErrors(getFieldErrors(err)); toast.error(getErrorMessage(err)); } finally { setSaving(false); }
  };

  const remove = async (row) => {
    if (!await confirmAction(`Delete ${row.code}?`)) return;
    try { await api.delete(`/pipeline/${cfg.endpoint}/${row.id}`); toast.success("Deleted"); load(); } catch (err) { toast.error(getErrorMessage(err)); }
  };

  const openActForm = (a, row) => {
    setActData({ ...blank(a.form.fields), ...(a.form.initial ? a.form.initial(row) : {}) });
    setActErr({}); setActMsg(""); setAct({ a, row });
  };
  const submitAct = async (e) => {
    e.preventDefault(); setActErr({}); setActMsg(""); setActBusy(true);
    const body = {};
    act.a.form.fields.forEach((fd) => {
      const v = actData[fd.key];
      body[fd.key] = fd.type === "number" ? (v === "" || v == null ? null : Number(v)) : (v === "" ? null : v);
    });
    try {
      const msg = await act.a.run(act.row, ctx, body);
      toast.success(msg || "Done"); setAct(null); load();
    } catch (err) { setActErr(getFieldErrors(err)); setActMsg(getErrorMessage(err)); toast.error(getErrorMessage(err), { duration: 9000 }); } finally { setActBusy(false); }
  };

  const runAction = async (a, row, extra) => {
    try { const msg = await a.run(row, ctx, extra); toast.success(msg || "Done"); load(); } catch (err) { toast.error(getErrorMessage(err), { duration: 9000 }); }
  };

  return (
    <div className="space-y-6" data-testid={`${cfg.endpoint}-page`}>
      {embedded ? (
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground">{cfg.description}</p>
          {canEdit && <Button onClick={openNew} className="shrink-0" data-testid={`new-${cfg.endpoint}-button`}><Plus className="h-4 w-4" /> New {cfg.singular}</Button>}
        </div>
      ) : (
        <>
          <PageHeader title={cfg.title} description={cfg.description} action={canEdit && <Button onClick={openNew} data-testid={`new-${cfg.endpoint}-button`}><Plus className="h-4 w-4" /> New {cfg.singular}</Button>} />
          <ClientFlowBar current={cfg.endpoint} version={rows} />
          <PageStatCards loading={loading} stats={cfg.stats(rows)} />
        </>
      )}
      <TableToolbar search={search} onSearch={setSearch} csvData={shown.map((r) => cfg.csv(r, clientName))} csvFilename={`${cfg.endpoint}.csv`}
        filters={[{ key: "status", placeholder: "All statuses", value: filter, onChange: setFilter, options: cfg.states.map((x) => ({ value: x, label: x.replaceAll("_", " ") })) }]} />
      <div className="rounded-xl border border-border bg-card">
        {!loading && (rows.length === 0 || (embedded && shown.length === 0 && !search && !filter)) ? <EmptyState title={embedded ? `Nothing at this step` : `No ${cfg.title.toLowerCase()} yet`} testId={`${cfg.endpoint}-empty`} /> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Code</TableHead><TableHead>Title</TableHead><TableHead>Client</TableHead>
              {cfg.columns.map((c) => <TableHead key={c.label}>{c.label}</TableHead>)}
              <TableHead>Status</TableHead><TableHead className="sticky right-0 z-10 bg-secondary text-right shadow-[-6px_0_6px_-6px_rgba(0,0,0,0.15)]">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {shown.map((r) => (
                <TableRow key={r.id} data-testid={`${cfg.endpoint}-row-${r.code}`}>
                  <TableCell className="font-mono text-xs">{r.code}</TableCell>
                  <TableCell className="font-medium text-foreground">{r.title}</TableCell>
                  <TableCell className="text-muted-foreground">{clientName(r.client_id)}</TableCell>
                  {cfg.columns.map((c) => <TableCell key={c.label} className="text-xs">{c.render(r, ctx)}</TableCell>)}
                  <TableCell><StateBadge value={r[cfg.stateField]} /></TableCell>
                  <TableCell className="sticky right-0 z-10 bg-card shadow-[-6px_0_6px_-6px_rgba(0,0,0,0.15)]">
                    <div className="flex items-center justify-end gap-1">
                      {(() => {
                        const nextLabel = cfg.next ? cfg.next(r) : null;
                        const list = canEdit ? (cfg.actions || []).filter((a) => !a.show || a.show(r)) : [];
                        const ordered = [...list.filter((a) => a.label === nextLabel), ...list.filter((a) => a.label !== nextLabel)];
                        return ordered.map((a) => {
                          const primary = a.label === nextLabel;
                          return (
                            <Button key={a.label} size="sm" variant={primary ? "default" : "outline"} data-testid={primary ? "next-step-button" : undefined}
                              className={primary ? "h-7 gap-1 px-2.5 text-xs" : "h-7 px-2 text-xs"}
                              title={primary ? "Next step for this record" : undefined}
                              onClick={async () => (a.dialog ? setCustom({ a, row: r }) : a.form ? openActForm(a, r) : a.needsProject ? (setPick({ a, row: r }), setPickId("")) : (a.confirm && !await confirmAction(a.confirm(r))) ? null : runAction(a, r))}>
                              {primary ? <>Next: {a.label} <ArrowRight className="h-3 w-3" /></> : a.label}
                            </Button>
                          );
                        });
                      })()}
                      {canEdit && <RowActions onEdit={() => openEdit(r)} moreItems={[...(cfg.moreItems ? cfg.moreItems(r) : []), { label: "Delete", icon: Trash2, onClick: () => remove(r), danger: true }]} />}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className={`max-h-[88vh] overflow-y-auto ${cfg.fields.some((f) => f.type === "items") ? "sm:max-w-6xl" : "sm:max-w-xl"}`}>
          <DialogHeader><DialogTitle>{dialog?.row ? `Edit ${dialog.row.code}` : `New ${cfg.singular}`}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-2 gap-3">
            {cfg.fields.map((f) => <Field key={f.key} f={f} form={form} setForm={setForm} ctx={ctx} errors={errors} />)}
            <DialogFooter className="col-span-2">
              {dialog?.row && cfg.endpoint === "proposals" && (
                <>
                  <Button type="button" variant="outline" onClick={() => openPdf(`/pipeline/proposals/${dialog.row.id}/pdf`, { view: "client" })}>Client quotation PDF</Button>
                  <Button type="button" variant="outline" onClick={() => openPdf(`/pipeline/proposals/${dialog.row.id}/pdf`, { view: "internal" })}>Internal cost PDF</Button>
                </>
              )}
              <Button type="submit" disabled={saving}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {custom && <custom.a.dialog row={custom.row} onClose={() => setCustom(null)} onDone={load} />}

      <Dialog open={!!act} onOpenChange={(o) => !o && setAct(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="action-dialog">
          <DialogHeader><DialogTitle>{act?.a.form.title || act?.a.label}{act ? ` · ${act.row.code}` : ""}</DialogTitle></DialogHeader>
          {act?.a.form.help && <p className="text-sm text-muted-foreground">{act.a.form.help(act.row)}</p>}
          <form onSubmit={submitAct} className="grid grid-cols-2 gap-3">
            {act?.a.form.fields.map((f) => <Field key={f.key} f={f} form={actData} setForm={setActData} ctx={ctx} errors={actErr} />)}
            {actMsg && <p className="col-span-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert" data-testid="action-error">{actMsg}</p>}
            <DialogFooter className="col-span-2">
              <Button type="submit" disabled={actBusy} data-testid="action-submit">{actBusy && <Loader2 className="h-4 w-4 animate-spin" />} {act?.a.form.submit || "Save"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pick} onOpenChange={(o) => !o && setPick(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{pick?.a.label}</DialogTitle></DialogHeader>
          <Label>Project</Label>
          <Select value={pickId} onValueChange={setPickId}>
            <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select project" /></SelectTrigger>
            <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.code} · {p.name}</SelectItem>)}</SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">The contract value is copied to the project, so Portfolio value and RA billing use it.</p>
          <DialogFooter><Button disabled={!pickId} onClick={async () => { const p = pick; setPick(null); await runAction(p.a, p.row, pickId); }}>Link</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
